import type {
  CompletePasswordResetRequest,
  EncryptedPasswordResetRequest,
} from './types';

const AES_GCM_IV_LENGTH_BYTES = 12;
const AES_GCM_AUTH_TAG_LENGTH_BYTES = 16;

function stripPemHeaders(pem: string) {
  return pem
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s+/g, '');
}

function base64ToArrayBuffer(value: string) {
  const decodedValue = window.atob(value);
  const bytes = new Uint8Array(decodedValue.length);

  for (let index = 0; index < decodedValue.length; index += 1) {
    bytes[index] = decodedValue.charCodeAt(index);
  }

  return bytes.buffer;
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  let output = '';

  for (const byte of bytes) {
    output += String.fromCharCode(byte);
  }

  return window.btoa(output);
}

export async function encryptPasswordResetPayload({
  payload,
  publicKey,
}: {
  payload: CompletePasswordResetRequest;
  publicKey: string;
}): Promise<EncryptedPasswordResetRequest> {
  if (!window.crypto?.subtle) {
    throw new Error(
      'Your browser could not prepare the secure password reset form. Please refresh the page and try again.'
    );
  }

  const importedPublicKey = await window.crypto.subtle.importKey(
    'spki',
    base64ToArrayBuffer(stripPemHeaders(publicKey)),
    {
      hash: 'SHA-256',
      name: 'RSA-OAEP',
    },
    false,
    ['encrypt']
  );
  const aesKey = await window.crypto.subtle.generateKey(
    {
      length: 256,
      name: 'AES-GCM',
    },
    true,
    ['encrypt']
  );
  const iv = window.crypto.getRandomValues(
    new Uint8Array(AES_GCM_IV_LENGTH_BYTES)
  );
  const serializedPayload = new TextEncoder().encode(JSON.stringify(payload));
  const encryptedPayloadBuffer = await window.crypto.subtle.encrypt(
    {
      iv,
      name: 'AES-GCM',
    },
    aesKey,
    serializedPayload
  );
  const exportedAesKey = await window.crypto.subtle.exportKey('raw', aesKey);
  const encryptedKeyBuffer = await window.crypto.subtle.encrypt(
    {
      name: 'RSA-OAEP',
    },
    importedPublicKey,
    exportedAesKey
  );
  const encryptedPayloadBytes = new Uint8Array(encryptedPayloadBuffer);
  const authTag = encryptedPayloadBytes.slice(
    encryptedPayloadBytes.length - AES_GCM_AUTH_TAG_LENGTH_BYTES
  );
  const encryptedBody = encryptedPayloadBytes.slice(
    0,
    encryptedPayloadBytes.length - AES_GCM_AUTH_TAG_LENGTH_BYTES
  );

  return {
    authTag: arrayBufferToBase64(authTag.buffer),
    encryptedKey: arrayBufferToBase64(encryptedKeyBuffer),
    iv: arrayBufferToBase64(iv.buffer),
    payload: arrayBufferToBase64(encryptedBody.buffer),
  };
}
